import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, curSym, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, LinkSimple } from "@phosphor-icons/react";
import { LockedPeriodBanner } from "@finance/components/LockedPeriodBanner";
import { CurrencySelect } from "@finance/components/CurrencySelect";

export default function Allocations() {
  const [direction, setDirection] = useState("payable");
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [activePayment, setActivePayment] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({});
  const [banks, setBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const load = useCallback(async () => {
    const [p, i] = await Promise.all([
      api.get("/allocations/payments", { params: { direction } }),
      api.get("/allocations/invoices", { params: { direction } }),
    ]);
    setPayments(p.data); setInvoices(i.data);
  }, [direction]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/bank-accounts").then((r) => setBanks(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {});
  }, []);

  const spotRate = (code) => { const c = currencies.find((x) => x.code === code); return c?.latest_rate ? Math.round(Number(c.latest_rate) * 10000) / 10000 : ""; };
  const setPayCurrency = (v) => setPayForm({ ...payForm, currency: v, fx_rate: v !== "TZS" ? spotRate(v) : "" });
  const payIsFx = payForm.currency && payForm.currency !== "TZS";

  const openPayment = async (p) => {
    setActivePayment(p);
    const r = await api.get("/allocations", { params: { payment_id: p.id } });
    setAllocs(r.data);
  };

  const allocate = async (invoice, amount) => {
    try {
      await api.post("/allocations", { payment_id: activePayment.id, invoice_type: invoice.invoice_type, invoice_id: invoice.invoice_id, amount: Number(amount) });
      toast.success("Allocated");
      await load(); openPayment({ ...activePayment });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeAlloc = async (id) => {
    try { await api.delete(`/allocations/${id}`); toast.success("Allocation removed"); await load(); openPayment(activePayment); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const createPayment = async () => {
    if (!payForm.amount) { toast.error("Amount required"); return; }
    if (!payForm.bank_account_id) { toast.error("Bank account is required"); return; }
    if (payIsFx && !(Number(payForm.fx_rate) > 0)) { toast.error(`Enter the FX rate (1 ${payForm.currency} = ? TZS) so this payment converts to the base currency.`); return; }
    try {
      const res = await api.post("/payments", {
        amount: Number(payForm.amount), currency: payForm.currency || "TZS",
        fx_rate: payIsFx ? Number(payForm.fx_rate) : 1,
        direction, payee: payForm.payee, payment_method: "bank",
        batch_no: payForm.reference,
        bank_account_id: payForm.bank_account_id,
        payment_date: payForm.payment_date || undefined,
      });
      if (res.data?.pending_cash_request) { toast.info(res.data.message); setPayOpen(false); setPayForm({}); return; }
      toast.success(direction === "payable" ? "Payment recorded" : "Receipt recorded");
      setPayOpen(false); setPayForm({}); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openInvoices = invoices.filter((i) => i.remaining > 0.01 && (
    !activePayment ? true
      : activePayment.party_id ? i.party_id === activePayment.party_id
      : activePayment.payee ? i.party === activePayment.payee
      : true
  ));

  return (
    <Layout>
      <PageHeader
        title="Allocations"
        subtitle="Match payments & receipts to specific invoices (partial or full)"
        actions={
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-payment-btn" onClick={() => { setPayForm({ currency: "TZS", payment_date: new Date().toISOString().slice(0, 10) }); setPayOpen(true); }}>
            <Plus size={16} className="mr-1" />{direction === "payable" ? "Record Payment" : "Record Receipt"}
          </Button>
        }
      />
      <div className="p-8">
        <div className="mb-4 flex gap-2">
          <button data-testid="tab-payable" onClick={() => { setDirection("payable"); setActivePayment(null); }} className={`rounded-sm px-3 py-1.5 text-sm ${direction === "payable" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>Supplier Payments (AP)</button>
          <button data-testid="tab-receivable" onClick={() => { setDirection("receivable"); setActivePayment(null); }} className={`rounded-sm px-3 py-1.5 text-sm ${direction === "receivable" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>Client Receipts (AR)</button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 lx-label">{direction === "payable" ? "Payments" : "Receipts"}</div>
            <table className="w-full text-sm">
              <thead><tr className="lx-label border-b border-gray-100 text-gray-400"><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Payee</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-right">Unallocated</th></tr></thead>
              <tbody>
                {payments.length === 0 && <TableEmpty colSpan={4} title="Nothing to allocate" message={`No ${direction === "payable" ? "payments" : "receipts"} yet.`} />}
                {payments.map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`} className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${activePayment?.id === p.id ? "bg-orange-50" : ""}`} onClick={() => openPayment(p)}>
                    <td className="px-4 py-2 lx-num">{fmtDate(p.payment_date)}</td>
                    <td className="px-4 py-2">{p.payee || "—"}</td>
                    <td className="px-4 py-2 text-right lx-num">{curSym(p.currency)} {fmtNum(p.amount)}</td>
                    <td className={`px-4 py-2 text-right lx-num font-semibold ${p.unallocated > 0.01 ? "text-amber-600" : "text-emerald-600"}`}>{fmtNum(p.unallocated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-sm border border-gray-200 bg-white">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 lx-label">{activePayment ? `Allocate ${activePayment.currency} ${fmtNum(activePayment.unallocated)} unallocated` : "Select a payment to allocate"}</div>
            {activePayment && (
              <div className="p-4">
                {allocs.length > 0 && (
                  <div className="mb-3">
                    <div className="lx-label mb-1 text-gray-500">Current allocations</div>
                    {allocs.map((a) => {
                      const inv = invoices.find((i) => i.invoice_id === a.invoice_id);
                      return (
                        <div key={a.id} className="flex items-center justify-between border-b border-gray-50 py-1 text-sm">
                          <span>{inv?.ref || a.invoice_id.slice(0, 8)} · {inv?.party || ""}</span>
                          <span className="flex items-center gap-2 lx-num">{fmtNum(a.amount)}<button className="text-gray-300 hover:text-red-500" data-testid={`remove-alloc-${a.id}`} onClick={() => removeAlloc(a.id)}><Trash size={13} /></button></span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="lx-label mb-1 text-gray-500">Open invoices for {activePayment.payee || "this party"}</div>
                <div className="max-h-[360px] space-y-2 overflow-y-auto">
                  {openInvoices.length === 0 && <div className="text-sm text-gray-400">No open invoices for {activePayment.payee || "this party"} with a remaining balance.</div>}
                  {openInvoices.map((inv) => (
                    <InvoiceAllocRow key={inv.invoice_id} inv={inv} maxAmt={Math.min(activePayment.unallocated, inv.remaining)} onAllocate={(amt) => allocate(inv, amt)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-display">{direction === "payable" ? "Record Payment" : "Record Receipt"}</DialogTitle></DialogHeader>
          <LockedPeriodBanner date={payForm.payment_date} className="mb-2" />
          <div className="space-y-4">
            <div>
              <Label className="lx-label">{direction === "payable" ? "Supplier / Payee" : "Client / Payer"}</Label>
              <SearchableSelect className="mt-1" testid="payment-payee" placeholder="Select"
                value={payForm.payee || ""} onValueChange={(v) => setPayForm({ ...payForm, payee: v })}
                options={(direction === "payable" ? suppliers : clients).map((p) => ({ value: p.name, label: p.name }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="lx-label">Amount</Label><Input type="number" className="mt-1" data-testid="payment-amount" value={payForm.amount || ""} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
              <div><Label className="lx-label">Currency</Label>
                <CurrencySelect value={payForm.currency || "TZS"} onValueChange={setPayCurrency} testid="payment-currency" className="mt-1" />
              </div>
            </div>
            {payIsFx && (
              <div className="rounded-sm border border-amber-200 bg-amber-50/50 p-3" data-testid="payment-fx-row">
                <Label className="lx-label text-amber-700">FX Rate — 1 {payForm.currency} = ? TZS</Label>
                <div className="mt-1 flex items-center gap-3">
                  <Input type="number" className="w-40" data-testid="payment-fx" value={payForm.fx_rate ?? ""} onChange={(e) => setPayForm({ ...payForm, fx_rate: e.target.value })} placeholder="e.g. 2600" />
                  <span className="text-sm text-gray-600">≈ <b className="lx-num">TSh {fmtNum((Number(payForm.amount) || 0) * (Number(payForm.fx_rate) || 0))}</b></span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="lx-label">Bank Account <span className="text-red-500">*</span></Label>
                <Select value={payForm.bank_account_id || ""} onValueChange={(v) => setPayForm({ ...payForm, bank_account_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="payment-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent>
                    {banks.length === 0 && <SelectItem value="none" disabled>No bank accounts — add one in Bank Reconciliation</SelectItem>}
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(() => {
                  const sb = banks.find((b) => b.id === payForm.bank_account_id);
                  const payCur = payForm.currency || "TZS";
                  return sb && (sb.currency || "TZS") !== payCur ? (
                    <p className="mt-1 text-xs font-medium text-amber-600" data-testid="payment-currency-mismatch">
                      Currency mismatch: paying {payCur} from a {sb.currency || "TZS"} account.
                    </p>
                  ) : null;
                })()}
              </div>
              <div><Label className="lx-label">Posting Date</Label><DateField className="mt-1" data-testid="payment-date" value={payForm.payment_date || ""} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            </div>
            <div><Label className="lx-label">Reference</Label><Input className="mt-1" data-testid="payment-reference" value={payForm.reference || ""} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-payment" onClick={createPayment}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function InvoiceAllocRow({ inv, maxAmt, onAllocate }) {
  const [amt, setAmt] = useState("");
  return (
    <div className="rounded-sm border border-gray-200 p-2" data-testid={`invoice-${inv.invoice_id}`}>
      <div className="flex items-center justify-between text-sm">
        <div><span className="font-medium">{inv.ref}</span><div className="text-xs text-gray-400">{inv.party}</div></div>
        <div className="text-right text-xs text-gray-500">Total {fmtNum(inv.total)}<div className="lx-num font-semibold text-amber-600">Remaining {fmtNum(inv.remaining)}</div></div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input type="number" className="h-8" placeholder={`Max ${fmtNum(maxAmt)}`} data-testid={`alloc-amount-${inv.invoice_id}`} value={amt} onChange={(e) => setAmt(e.target.value)} />
        <Button size="sm" variant="outline" data-testid={`alloc-full-${inv.invoice_id}`} onClick={() => setAmt(String(maxAmt))}>Full</Button>
        <Button size="sm" className="bg-gray-900 hover:bg-gray-700" data-testid={`alloc-apply-${inv.invoice_id}`} onClick={() => { if (amt) { onAllocate(amt); setAmt(""); } }}><LinkSimple size={14} className="mr-1" />Apply</Button>
      </div>
    </div>
  );
}
